/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { combineReducers } from "redux";

import accessControlReducer from "Dataflows/AccessControl/AccessControlReducer";
import activityAndEventTypesReducer from "Dataflows/ActivityEventTypes/ActivityEventTypesReducer";
import applicationReducer from "Dataflows/Application/ApplicationReducer";
import applicationDetailsReducer from "Dataflows/ApplicationDetails/ApplicationDetailsReducer";
import awardCategoriesReducer from "Dataflows/AwardCategories/AwardCategoriesSlice";
import awardKitSkuReducer from "Dataflows/AwardKitSku/AwardKitSkuReducer";
import awardPeriodReducer from "Dataflows/AwardPeriod/AwardPeriodReducer";
import azureBenefitRequestsReducer from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsReducer";
import benefitReducer from "Dataflows/Benefit/BenefitReducer";
import manageCodesReducer from "Dataflows/Benefit/ManageCodesSlice";
import CommonDataReducer from "Dataflows/CommonData/CommonDataReducer";
import demographicsReducer from "Dataflows/Demographics/DemographicsReducer";
import distributionListsReducer from "Dataflows/DistributionLists/DistributionListsReducer";
import m365CopilotBenefitReducer from "Dataflows/M365CopilotBenefit/M365CopilotBenefitReducer";
import mvpReducer from "Dataflows/MVP/MVPReducer";
import rdUserProfileReducer from "Dataflows/RD/UserProfile/RDUserProfileReducer";
import regionReducer from "Dataflows/Region/RegionReducer";
import UpdateSAProgramStatusReducer from "Dataflows/SA/UpdateSAProgramStatus/UpdateSAProgramStatusReducer";
import UpdateSARegistrationStatusReducer from "Dataflows/SA/UpdateSARegistrationStatus/UpdateSARegistrationStatusReducer";
import technologyAreaReducer from "Dataflows/TechnologyArea/TechnologyAreaReducer";
import { awardCategoriesApi } from "Services/AwardCategoriesApi";
import { manageCodesApi } from "Services/ManageCodesApi";
import { benefitManagementApi } from "Services/BenefitManagementApi";
import clRegionReducer from "../ClRegion/ClRegionsReducer";
import countryRegionReducer from "../CountryRegion/CountryRegionReducer";
import eventReducer from "../Event/EventReducer";
import industryFocusReducer from "../IndustryFocus/IndustryFocusReducer";
import programLanguageDataReducer from "../ProgramLanguage/ProgramLanguageReducer";
import schoolDataReducer from "../School/SchoolDataReducer";
import socialNetworkDataReducer from "../SocialNetwork/SocialNetworkReducer";
import stateProvinceReducer from "../StateProvince/StateProvinceReducer";
import technicalExpertiseReducer from "../TechnicalExpertise/TechnicalExpertiseReducer";
import userProfileReducer from "../UserProfile/UserProfileReducer";
import visualStudioBenefitReducer from "../VisualStudioBenefit/VisualStudioBenefitReducer";

// Handles multiple reducers
const rootReducer = combineReducers({
  awardPeriodDataState: awardPeriodReducer,
  awardKitSkuDataState: awardKitSkuReducer,
  accessControlState: accessControlReducer,
  applicationState: applicationReducer,
  applicationDetailsState: applicationDetailsReducer,
  azureBenefitRequestsState: azureBenefitRequestsReducer,
  m365CopilotBenefitState: m365CopilotBenefitReducer,
  benefitState: benefitReducer,
  clRegionsDataState: clRegionReducer,
  countryRegionDataState: countryRegionReducer,
  eventState: eventReducer,
  industryFocusDataState: industryFocusReducer,
  mvpState: mvpReducer,
  saRegistrationStatusState: UpdateSARegistrationStatusReducer,
  saProgramStatusState: UpdateSAProgramStatusReducer,
  rdUserProfileState: rdUserProfileReducer,
  regionDataState: regionReducer,
  schoolDataState: schoolDataReducer,
  socialNetworkDataState: socialNetworkDataReducer,
  programLanguageDataState: programLanguageDataReducer,
  stateProvinceDataState: stateProvinceReducer,
  technicalExpertiseDataState: technicalExpertiseReducer,
  technologyAreaDataState: technologyAreaReducer,
  activityEventTypesState: activityAndEventTypesReducer,
  userProfileState: userProfileReducer,
  visualStudioBenefitState: visualStudioBenefitReducer,
  distributionListsState: distributionListsReducer,
  demographicsState: demographicsReducer,
  commonDataState: CommonDataReducer,
  awardCategories: awardCategoriesReducer,
  [awardCategoriesApi.reducerPath]: awardCategoriesApi.reducer,
  [manageCodesApi.reducerPath]: manageCodesApi.reducer,
  manageCodes: manageCodesReducer,
  [benefitManagementApi.reducerPath]: benefitManagementApi.reducer,
});

export default rootReducer;
export type AppState = ReturnType<typeof rootReducer>;
